import React from 'react';
import styled from 'styled-components';
import { fontFamily } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';

/**
 * Full-width answer-scale option for the PHQ-9 / GAD-7 screener.
 *
 * Matches Boris's prototype `rg-scale-opt` 1:1: a rounded (15px) full-width
 * button that fills with near-black ink and shows a cream label when selected
 * (no radio glyph). Renders only the answer label — the underlying point value
 * / score is never surfaced to the user (RND-35692).
 */
const OptionButton = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 60px;
  padding: 8px 18px;
  border-radius: 15px;
  /* inkSoft (not line/lineStrong) for the resting border: line ≈1.3:1 and
     lineStrong ≈1.6:1 on white, both under the WCAG 1.4.11 3:1 minimum for
     non-text UI boundaries; inkSoft solid ≈6.4:1 clears it. */
  border: 1.5px solid ${({ $selected }) => ($selected ? BorisScreener.ink : BorisScreener.inkSoft)};
  background-color: ${({ $selected }) => ($selected ? BorisScreener.ink : '#ffffff')};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.14s ease, border-color 0.14s ease;

  &:hover {
    border-color: ${BorisScreener.ink};
  }
`;

const Label = styled.span<{ $selected: boolean }>`
  font-family: ${fontFamily};
  font-size: 17px;
  font-weight: 500;
  color: ${({ $selected }) => ($selected ? BorisScreener.cream : BorisScreener.ink)};
`;

export type ScaleOptionProps = {
  value: string;
  label: string;
  selected: boolean;
  onClick: (value: string) => void;
};

const ScaleOption: React.FC<ScaleOptionProps> = ({ value, label, selected, onClick }) => (
  <OptionButton
    type="button"
    role="radio"
    aria-checked={selected}
    aria-label={label}
    $selected={selected}
    onClick={() => onClick(value)}
  >
    <Label $selected={selected} aria-hidden="true">
      {label}
    </Label>
  </OptionButton>
);

export default ScaleOption;
